import React, { useMemo, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  Paper,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import { CloudUploadIcon } from 'lucide-react';
import { useDropzone } from 'react-dropzone/.';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';

// ─── STANDARD TEXT / NUMBER INPUT ───
export const FormInput = ({ name, label, type = 'text', ...props }) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          {...props}
          type={type}
          label={label}
          size="small"
          fullWidth
          error={!!error}
          helperText={error?.message || props.helperText}
        />
      )}
    />
  );
};

export const FormSelect = ({ name, label, options = [], disabled = false, ...props }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth variant="outlined" size="small" error={!!error} disabled={disabled}>
          <InputLabel id={`${name}-select-label`}>{label}</InputLabel>
          <Select
            {...field}
            value={field.value ?? ''}
            labelId={`${name}-select-label`}
            id={`${name}-select`}
            label={label}
            {...props}>
            {/* Fallback empty option so users can clear or read default state */}
            <MenuItem value="">
              <em>-- Select Type --</em>
            </MenuItem>

            {options.map(option => (
              <MenuItem
                key={option.id || option.value || option.label || option}
                value={option.id || option.value || option.label || option}>
                {option.title || option.label || option}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

export default FormSelect;

// ─── DATE PICKER WRAPPER ───
export const FormDatepicker = ({ name, label, ...props }) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          {...props}
          type="datetime-local"
          label={label}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );
};

// ─── AUTOCOMPLETE SELECT (SINGLE SELECTION) ───
export const FormAutocomplete = ({ name, label, data = [], ...props }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // Find matching structural object if data contains option mappings
        const selectedOption =
          data.find(opt => (opt && typeof opt === 'object' ? (opt.value ?? opt.label) === value : opt === value)) || null;

        return (
          <Autocomplete
            options={data}
            value={selectedOption}
            // Handle getting either the raw object option value or the flat option string
            getOptionLabel={option => {
              if (!option) return '';
              if (typeof option === 'object') return option.label || '';
              return option;
            }}
            // Determine equality checks based on tracking keys
            isOptionEqualToValue={(option, val) => {
              const optVal = typeof option === 'object' ? option.value ?? option.label : option;
              const compareVal = typeof val === 'object' ? val.value ?? val.label : val;
              return optVal === compareVal;
            }}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue === 'object') {
                onChange(newValue.value ?? newValue.label ?? newValue);
              } else {
                onChange(newValue);
              }
            }}
            renderInput={params => (
              <TextField {...params} label={label} error={!!error} helperText={error?.message} size="small" />
            )}
            {...props}
          />
        );
      }}
    />
  );
};

// ─── MULTI-SELECT AUTOCOMPLETE WRAPPER ───
export const FormMultiSelect = ({ name, label, data = [], ...props }) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Autocomplete
          multiple
          options={data}
          value={Array.isArray(value) ? value : []}
          onChange={(_, newValue) => onChange(newValue)}
          renderInput={params => (
            <TextField {...params} label={label} size="small" fullWidth error={!!error} helperText={error?.message} />
          )}
          {...props}
        />
      )}
    />
  );
};

// ─── MULTI-SELECT AUTOCOMPLETE WITH PIPE-SEPARATION ───
export const FormAutocompleteMultiSelect = ({ name, label, data = [], ...props }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // 1. DESERIALIZE: Safe, inline calculation without violating hook rules
        let currentFormValues = [];
        if (Array.isArray(value)) {
          currentFormValues = value;
        } else if (typeof value === 'string' && value.trim() !== '') {
          currentFormValues = value.split('|').map(v => v.trim());
        }

        // 2. LOOKUP: Match values against the options dataset
        const selectedOptions = currentFormValues.map(val => {
          return (
            data.find(opt => {
              if (opt && typeof opt === 'object') {
                return String(opt.value ?? opt.label) === String(val);
              }
              return String(opt) === String(val);
            }) || val
          );
        });

        return (
          <Autocomplete
            multiple
            options={data}
            value={selectedOptions}
            getOptionLabel={option => {
              if (!option) return '';
              if (typeof option === 'object') return option.label || '';
              return option;
            }}
            isOptionEqualToValue={(option, val) => {
              const optVal = typeof option === 'object' ? option.value ?? option.label : option;
              const compareVal = typeof val === 'object' ? val.value ?? val.label : val;
              return String(optVal) === String(compareVal);
            }}
            // 3. SERIALIZE: Convert array back to a pipe string "A|B" for form submission
            onChange={(_, newValues) => {
              const extractedPrimitives = (newValues || []).map(item => {
                if (item && typeof item === 'object') {
                  return item.value ?? item.label ?? item;
                }
                return item;
              });

              // Join with pipes, or send an empty string if nothing is selected
              onChange(extractedPrimitives.length > 0 ? extractedPrimitives.join('|') : '');
            }}
            renderInput={params => (
              <TextField {...params} label={label} size="small" fullWidth error={!!error} helperText={error?.message} />
            )}
            {...props}
          />
        );
      }}
    />
  );
};

export const DropzoneUpload = ({
  label,
  onFileSelect,
  multiple = false,
  required = false,
  uploadedFiles = null, // existing server files
  onFileDelete, // delete server file
  onFileView, // view/download server file
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple,
    onDrop: acceptedFiles => {
      const files = multiple ? acceptedFiles : [acceptedFiles[0]];
      setSelectedFiles(files);
      if (onFileSelect) {
        onFileSelect(multiple ? files : files[0]);
      }
    },
  });

  return (
    <Box
      sx={{
        '& .MuiTableCell-root': { fontSize: '0.825rem' },
        '& .MuiInputBase-root': { fontSize: '0.825rem !important' },
        '& .MuiTypography-root': { fontSize: '0.825rem !important' },
      }}>
      <Typography variant="subtitle1" gutterBottom>
        {label}{' '}
        {required && (
          <Typography component="span" color="error">
            *
          </Typography>
        )}
      </Typography>

      <Paper
        {...getRootProps()}
        elevation={0}
        sx={{
          border: '2px dashed #ccc',
          backgroundColor: isDragActive ? '#f0faff' : 'transparent',
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          minHeight: 150,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
        <input required={required} {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 40, mb: 1, color: 'gray', mx: 'auto' }} />
        <Typography variant="body1" fontWeight="bold">
          Browse Files
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or drag and drop files here
        </Typography>
      </Paper>

      {/* Newly selected local files */}
      <Box mt={1} sx={{ display: 'flex', overflowX: 'auto', gap: 1, py: 1, px: 0.5 }}>
        {selectedFiles.map((file, index) => (
          <Tooltip key={index} title={file.name}>
            <Chip
              label={file.name.length > 20 ? file.name.slice(0, 18) + '…' : file.name}
              color="primary"
              sx={{ fontSize: '0.7rem', height: '24px', px: 1 }}
            />
          </Tooltip>
        ))}
      </Box>

      {console.log('UPLOADED FILES IN DROPZONE ===', uploadedFiles)}

      {/* Existing server files */}
      {uploadedFiles?.length > 0 && (
        <Box mt={1} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="textSecondary" fontWeight="bold">
            Uploaded Files
          </Typography>
          {uploadedFiles.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                bgcolor: '#fafafa',
              }}>
              <Tooltip title={file.documentName}>
                <Typography
                  variant="caption"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 160,
                  }}>
                  {file.documentName}
                </Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                {onFileView && (
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={() => onFileView(file)}>
                      <DownloadIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
                {onFileDelete && (
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => onFileDelete(file)}>
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
